import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import ThemeToggle from "@/components/site/ThemeToggle";

const NAV_LINKS = [
  { href: "/#interventions", label: "Interventions" },
  { href: "/#missions", label: "Missions" },
  { href: "/#methode", label: "Méthode" },
  { href: "/#interventions-cas", label: "Exemples" },
  { href: "/#credibilite", label: "Crédibilité" },
  { href: "/cv", label: "CV", internal: true },
  { href: "/diagnostic", label: "Diagnostic SI", internal: true },
  { href: "/diagnostic-pca", label: "Diagnostic PCA", internal: true },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF8F5]/85 backdrop-blur-md border-b border-[#E2DCD0]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
        <a
          href="/"
          data-testid="header-logo"
          className="font-serif text-xl sm:text-2xl tracking-tight leading-none translate-y-[4px] text-[#0B132B] hover:text-[#16234A] transition-colors"
        >
          Olivier Soutarson
        </a>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-9">
          {NAV_LINKS.map((link) =>
            link.internal ? (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="whitespace-nowrap text-[12px] 2xl:text-[13px] uppercase tracking-[0.14em] text-[#C8A97E] hover:text-[#b5956a] underline-offset-[6px] hover:underline decoration-1 transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="whitespace-nowrap text-[12px] 2xl:text-[13px] uppercase tracking-[0.14em] text-[#171D2A] hover:text-[#C8A97E] transition-colors"
              >
                {link.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-4 2xl:gap-8">
          <ThemeToggle />
          <a
            href="/#contact"
            onClick={(e) => {
              if (window.location.pathname === "/") {
                const el = document.getElementById("contact");
                if (el) {
                  e.preventDefault();
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }
            }}
            data-testid="header-cta"
            className="inline-flex items-center gap-2 bg-[#0B132C] text-[#FAF8F5] px-6 py-3 text-xs uppercase tracking-[0.18em] font-medium hover:bg-[#16234A] transition-colors"
          >
            Échanger
          </a>
        </div>

        <div className="xl:hidden flex items-center gap-5">
          <ThemeToggle />
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            className="text-[#0B132B] p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-[#FAF8F5] border-t border-[#E2DCD0] px-6 py-6 space-y-4"
        >
          {NAV_LINKS.map((link) =>
            link.internal ? (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setOpen(false)}
                className="block text-sm uppercase tracking-[0.14em] text-[#C8A97E] hover:text-[#b5956a]"
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="block text-sm uppercase tracking-[0.14em] text-[#171D2A] hover:text-[#C8A97E]"
              >
                {link.label}
              </a>
            )
          )}
          <a
            href="/#contact"
            onClick={(e) => {
              setOpen(false);
              if (window.location.pathname === "/") {
                const el = document.getElementById("contact");
                if (el) {
                  e.preventDefault();
                  setTimeout(
                    () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
                    50
                  );
                }
              }
            }}
            data-testid="mobile-cta"
            className="inline-block bg-[#0B132C] text-[#FAF8F5] px-6 py-3 text-xs uppercase tracking-[0.18em] mt-2"
          >
            Échanger
          </a>
        </div>
      )}
    </header>
  );
}
